/**
 * @fileoverview A group that is a photo of several kinds of plant: a tray of seedlings, a bouquet, a harvest, or a
 * mixed bed. Instead of "What is this?", the plants Grow saw, each a row to tick, then the bed and one button, "Add 3
 * plants to My Garden". Styled as the seed packets card is (PacketPile). It was the bouquet card (BouquetPile) until
 * 2026-10-06.
 *
 * The photo's own guess said so (guess.kinds and guess.group), in the same one answer that names any photo. Each kind
 * is a row (D78 rev 2 §3, PacketRow, as the packet card's): tick · its own part of the photo · its name, with the
 * plant and variety it is filed as under it ("Vining Slicing Tomato · Cherokee Purple", from a plant tag in the
 * photo) · ›. Grow has a group for only a few flowers, so most go under a broad parent as the gardener's own variety
 * named for the flower ("Tender Annual Flower · Zinnia"); one they or Grow already have by that name is used, and a
 * new one is marked "New variety". A plant Grow does not have at all ("tomatillo") is marked "New to Grow": it is
 * added as the gardener's own plant, cared for like the plant it grows most like, and Grow reviews it.
 *
 * The picture is the kind's own part of the photo (the box the guess gave round it: a cell of that seedling, one
 * bloom of that flower), cut out as a seed packet's is (PacketCut). A picture only (B59). A kind with no box shows,
 * and gets, the whole photo.
 *
 * Tapping a row (anywhere but its tick) opens its change dialog, "Change this plant" (RowChangeDialog): the plant
 * fields with their search (Type with B52's "Not sure", Variety with the variety Grow saw, "Cherokee Purple (from the
 * tag)"), its bed ("Same as the others", "A new bed…", their beds: the bed line under the name moved here, D78 rev 2
 * §3), Done, and "Remove this plant". A row with a bed of its own says so under its name ("Herbs (its own bed)").
 * Changing the Bed list moves only the plants without a bed of their own; the button names every place they go
 * ("Add 2 plants: 1 to Side garden, 1 to Back bed", lib/bedPlaces).
 *
 * The Bed list has "A new bed…" too (D78 P4): a photo of a bed added from Beds starts on it (newBed). The bed is made
 * by the name typed, with this photo as its cover, and its kind, sun and cover are asked on its card, never guessed
 * ("This photo becomes Back bed's picture. Its kind, sun and cover are asked on the bed's card.").
 *
 * Adding files a plant for each ticked kind (POST /api/tray/kinds): each gets its own cut-out (with the whole photo as
 * the original it can be reset to) or the whole photo, dated as taken, at the stage that kind of photo means, and the
 * photo leaves the tray.
 *
 * At the top, "Grow sees 2 plants in this photo. Is that right?" with a switch (a radio group) between "2 plants" and
 * "1 plant". "1 plant" lists Grow's guesses as single choices, the first picked, and "Something else…" (which shows
 * the single-plant form PileCard passes in, somethingElse); a guess is filed with the Bed list's bed as one plant,
 * "Add Swiss chard". Switching back keeps every tick, picture and bed as it was.
 *
 * On Free, with more kinds ticked than Free has room for, the button offers what fits and the rest with Grow More
 * ("Add 2 now" and "Add all 4 with Grow More", FreeRoomChoice); the server adds the first ones, up to the room.
 *
 * Which variety a seen name is ("Sun Gold", a tag's "Cherokee Purple") is the server's (W24 Job B): each kind's name is
 * sent with its type (GET /plants/picker/varieties?match=, useSeenMatches), and the server says the variety of that name
 * under this type, else under the crop's other types; the card compares no names itself. The rows' type and crop names,
 * and their varieties, are the varieties answers of their types (usePickerLookup).
 *
 * Where: PileCard, for a photo the guess saw several kinds in.
 * API: GET /plants/picker/crops and /plants/picker/varieties (the names, the varieties and the matches); POST /tray/kinds (fileKinds: the tray id, the bed or a new one's name, items with their boxes and own beds,
 * the group, upToRoom).
 * Settings: Free's room (useFreeRoom); the server's limits.LOCATION_NAME_MAX (lib/growSettings).
 * Words (en/es): tray.kinds.* (noBed, noneTicked, newToGrow, newPlantNote, newPlantNoteOne, sees, nPlants, onePlant,
 * whichOne, somethingElse, addPlant, changeTitle, removeThis, ownBed), tray.pile.seenOnTag, tray.pile.seenByGrow,
 * review.coverNote, packets.newBed, packets.bedName, packets.why.bed, packets.bed, packets.saveFailed, packets.removed;
 * the rows' and the dialog's own.
 * A failed add says why (lib/errorMessage): the server's refusal, or for a failure on our side (a 500, no connection;
 * D31) the standard sentence, reported to Sentry, with the support address as a link; else packets.saveFailed.
 *
 * @module KindsPile
 */

import React from 'react';
import { Button } from '@/shared/components/ui/button';
import { AddCancelRow } from './AddCancelRow';
import { NativeSelect } from '@/shared/components/ui/native-select';
import { Input } from '@/shared/components/ui/input';
import Spinner from '@/shared/components/ui/Spinner';
import { useToast } from '@/shared/components/ui/use-toast';
import { useTranslation } from '@/shared/hooks/useTranslation';
import { errorMessage } from '@/shared/lib/errorMessage';
import { FailureText } from '@/shared/components/ServerFailure';
import {
  EMPTY_CHOICE, usePickerLookup, useSeenMatches, type CropChoice, type PickerLookup, type SeenMatches,
} from '@/shared/components/plant-fields/usePlantPicker';
import { fileKinds, type KindItem, type TrayGroup, type TrayKind, type TrayPhoto } from '@/shared/api/tray';
import type { PacketResult } from '@/shared/api/packets';
import type { BedName } from '@/shared/api/locations';
import { PacketCut, PacketThumb } from '@/features/packets/components/PacketPicture';
import { PacketRow } from '@/features/packets/components/PacketRow';
import { RowChangeDialog } from '@/features/packets/components/RowChangeDialog';
import { addLabelFor, bedPlaces } from '@/features/packets/lib/bedPlaces';
import type { OwnBed } from '@/features/packets/lib/packetRows';
import { useGrowLimits } from '@/shared/lib/growSettings';
import { FreeRoomChoice } from '@/features/billing/components/FreeRoom';
import { useFreeRoom } from '@/features/billing/lib/useFreeRoom';

/** What the card's kinds part is given. */
interface KindsPileProps {
  /** The photo: its kinds and what kind of photo it is are in its guess. */
  photo: TrayPhoto;
  /** The live beds. */
  locations: BedName[];
  /** True while the card is busy elsewhere. */
  busy: boolean;
  /** For the fields' ids. */
  idBase: string;
  /** The plants were filed: what was added, and to which bed. */
  onFiled: (result: PacketResult) => void;
  /** "Something else…" in the one-plant choice: the single-plant form, as the plant question shows it. */
  somethingElse: React.ReactNode;
  /** Added from Beds' photo row (D78 P2/P4): the Bed list starts on "A new bed…". */
  newBed?: boolean;
  /** Cancel, beside the Add buttons: what the photo's × does. */
  onCancel?: () => void;
}

/** A kind as the card shows and files it. */
interface PlacedKind {
  /** The plant's name: Grow's, or the new one's own. */
  plantName: string;
  /** The variety's name, when it is filed as one. */
  varietyName: string | null;
  /** A variety of theirs that will be made. */
  isNewVariety: boolean;
  /** A plant Grow does not have, made as theirs. */
  isNewPlant: boolean;
  /** What is sent to file it. */
  item: KindItem;
}

/** A row changed in its dialog: its plant, type and variety, and whether the variety Grow saw is the one chosen. */
interface KindEdit {
  /** The plant, type and variety chosen. */
  choice: CropChoice;
  /** The variety Grow saw is chosen (filed as theirs by that name). */
  seenChosen: boolean;
}

/** The "new bed" choice in the Bed list. */
const NEW_BED = 'new';

/**
 * Text with its first letter raised, to open a heading or a line.
 *
 * @param text - The words.
 */
const capital = (text: string): string => text.charAt(0).toUpperCase() + text.slice(1);

/**
 * A kind as the card shows and files it: the plant's name, and its variety (the one Grow or the gardener has by that
 * name under any type of that crop, as the server matched it, which then files it under that type; else a new one of
 * theirs); or a plant Grow does not have, by its own name.
 *
 * @param kind - The kind seen.
 * @param lookup - The rows' types' names (usePickerLookup).
 * @param seen - The server's matches of the names seen (useSeenMatches).
 * @returns The names the row shows, the new-variety and new-plant marks, and what is sent.
 */
function placed(kind: TrayKind, lookup: PickerLookup, seen: SeenMatches): PlacedKind {
  if (kind.plant_id === null || kind.plant_id === undefined) {
    const name = capital(kind.new?.name ?? kind.seen);
    return {
      plantName: name, varietyName: null, isNewVariety: false, isNewPlant: true,
      item: { newPlant: { name: kind.new?.name ?? kind.seen, likePlantId: kind.new?.likePlantId ?? 0 } },
    };
  }
  const plantId = kind.plant_id;
  const plantName = lookup.plantName(plantId) ?? capital(kind.seen);
  const base = { plantName, isNewPlant: false };
  if (!kind.variety) return { ...base, varietyName: null, isNewVariety: false, item: { plantId } };
  // Grow's or theirs by that name, under this type first, then the crop's other types: the server's match.
  const known = seen.matchOf(plantId, kind.variety);
  if (known) {
    return {
      ...base, plantName: known.plantName, varietyName: known.varietyName, isNewVariety: false,
      item: { plantId: known.plantId, varietyId: known.varietyId },
    };
  }
  return { ...base, varietyName: kind.variety, isNewVariety: true, item: { plantId, newVariety: kind.variety } };
}

/**
 * A row as its dialog starts it: the plant and variety it is filed as, the variety Grow saw chosen when it is filed by that name.
 *
 * @param row - The kind as placed.
 * @param lookup - The rows' types' crops and varieties (usePickerLookup).
 */
const startEdit = (row: PlacedKind, lookup: PickerLookup): KindEdit => {
  if (row.isNewPlant || row.item.plantId === undefined) return { choice: EMPTY_CHOICE, seenChosen: false };
  const plantId = row.item.plantId;
  return {
    choice: { cropId: lookup.cropOf(plantId), plantId, varietyId: row.item.varietyId ?? lookup.varietiesOf(plantId).start },
    seenChosen: Boolean(row.item.newVariety),
  };
};

/**
 * What a row is filed as once changed in its dialog: Type "Not sure" as the crop and the flag (B52, the server files
 * its default type); else the type, and the variety chosen, or Grow's seen name as theirs when that is chosen.
 *
 * @param edit - The change.
 * @param kind - The kind seen.
 * @param lookup - The rows' types' varieties (usePickerLookup), for where Variety starts.
 */
const editedItem = (edit: KindEdit, kind: TrayKind, lookup: PickerLookup): KindItem => {
  if (edit.choice.typeUnsure && edit.choice.cropId !== null) return { cropId: edit.choice.cropId, typeUnsure: true };
  const plantId = Number(edit.choice.plantId);
  const seen = edit.seenChosen && kind.variety && edit.choice.varietyId === lookup.varietiesOf(plantId).start;
  return seen ? { plantId, newVariety: kind.variety } : { plantId, varietyId: edit.choice.varietyId };
};

/**
 * The kinds part of a group's card: what kind of photo it is and the plants seen, each a row to tick or change, the
 * bed, and the button that adds them.
 *
 * @param props - KindsPileProps.
 */
export const KindsPile: React.FC<KindsPileProps> = ({ photo, locations, busy, idBase, onFiled, somethingElse, newBed: startNew = false, onCancel }) => {
  const { reactTranslate: t } = useTranslation();
  const { toast } = useToast();
  const limits = useGrowLimits();
  /** The kinds the guess saw in the photo, in its order. */
  const kinds = React.useMemo(() => photo.guess.kinds ?? [], [photo.guess.kinds]);
  // A photo guessed before it was asked what kind it is had kinds only when it was a bouquet.
  const group: Exclude<TrayGroup, 'one'> = photo.guess.group && photo.guess.group !== 'one' ? photo.guess.group : 'bouquet';

  // Every kind starts ticked.
  const [ticked, setTicked] = React.useState<Set<number>>(() => new Set(kinds.map((_, key) => key)));
  // The bed: a new one (added from Beds), the bed the photo was added from when it is still one of theirs, else their first.
  const [bedChoice, setBedChoice] = React.useState<string | null>(
    () => (startNew ? NEW_BED : locations.some((bed) => bed.id === photo.location_id) ? String(photo.location_id) : null)
  );
  const [bedName, setBedName] = React.useState('');
  // Several plants, or "1 plant"; and in "1 plant", the guess chosen (its place in the kinds) or something else.
  const [mode, setMode] = React.useState<'many' | 'one'>('many');
  const [pickOne, setPickOne] = React.useState<number | 'else'>(0);
  // The plants given a bed of their own, by place in the kinds; the rest follow the Bed list.
  const [own, setOwn] = React.useState<Record<number, OwnBed>>({});
  // The plants changed in their dialog, and the ones taken out, by place in the kinds.
  const [edits, setEdits] = React.useState<Record<number, KindEdit>>({});
  const [removed, setRemoved] = React.useState<Set<number>>(() => new Set());
  // The plant whose dialog is open, if any.
  const [opened, setOpened] = React.useState<number | null>(null);
  const [saving, setSaving] = React.useState(false);
  // Why the last add failed (lib/errorMessage's sentence), or null.
  const [failed, setFailed] = React.useState<string | null>(null);

  const choice = bedChoice ?? (locations[0] ? String(locations[0].id) : null);
  const newBed = choice === NEW_BED;
  const bedId = newBed || choice === null ? null : Number(choice);
  const bedLabel = newBed ? bedName.trim() : locations.find((bed) => bed.id === bedId)?.name ?? '';
  /** The bed every kind without its own goes to, as POST /tray/kinds names it; null with none chosen. */
  const sharedTarget: number | { newBedName: string } | null = newBed ? { newBedName: bedLabel } : bedId;
  /** Which variety each kind's seen name is, as the server matches it under the kind's type. */
  const seenRows = useSeenMatches(kinds.map((kind) => ({ plantId: kind.plant_id ?? null, name: kind.variety })));
  /** The names and varieties of every type a row is (or is changed to). */
  const lookup = usePickerLookup([
    ...kinds.map((kind) => kind.plant_id),
    ...kinds.map((kind) => seenRows.matchOf(kind.plant_id ?? null, kind.variety)?.plantId),
    ...Object.values(edits).map((edit) => edit.choice.plantId),
  ]);
  const rows = kinds.map((kind, key) => ({ kind, key, ...placed(kind, lookup, seenRows) }));
  const shown = rows.filter((row) => !removed.has(row.key));
  const chosen = shown.filter((row) => ticked.has(row.key));
  /**
   * A kind's own bed, when it was given one (null for the group's).
   *
   * @param key - The kind's row.
   * @returns Its own bed choice, or null.
   */
  const ownOf = (key: number): OwnBed | null => own[key] ?? null;
  // Each sent with its box for its own cut-out, or none for the whole photo, and its own bed.
  const items = chosen.map((row) => {
    const bed = ownOf(row.key);
    const edit = edits[row.key];
    return {
      ...(edit ? editedItem(edit, row.kind, lookup) : row.item),
      box: row.kind.box ?? null,
      ...(bed?.kind === 'bed' ? { locationId: bed.id } : bed?.kind === 'new' ? { newBedName: bed.name.trim() } : {}),
    };
  });
  // A new bed still without a name has nowhere to go yet.
  const unnamed = (newBed && !bedLabel) || chosen.some((row) => { const bed = ownOf(row.key); return bed?.kind === 'new' && !bed.name.trim(); });
  const blocked = sharedTarget === null ? t('tray.kinds.noBed') : chosen.length === 0 ? t('tray.kinds.noneTicked') : unnamed ? t('packets.why.bed') : null;
  const places = bedPlaces(chosen.map((row) => ownOf(row.key)), { id: bedId, newName: newBed ? bedLabel : null }, locations, t);
  // Nothing is filed until the server has named and matched every row.
  const off = saving || busy || lookup.loading || seenRows.loading;
  const newPlants = chosen.filter((row) => row.isNewPlant && !edits[row.key]);
  // On Free: more ticked than there is room for, so the button offers what fits.
  const free = useFreeRoom();
  const overRoom = free.limited && chosen.length > free.room;

  /**
   * Ticks or unticks one kind.
   *
   * @param key - Its place in the kinds.
   * @param on - Ticked.
   */
  const tick = (key: number, on: boolean): void => {
    setTicked((current) => {
      const next = new Set(current);
      if (on) next.add(key); else next.delete(key);
      return next;
    });
  };

  /**
   * Gives one plant its own bed, or puts it back with the others.
   *
   * @param key - Its place in the kinds.
   * @param bed - Its own bed, or null for the same as the others.
   */
  const setOwnBed = (key: number, bed: OwnBed | null): void => {
    setOwn((current) => {
      const next = { ...current };
      // The bed the others are in is no bed of its own.
      if (bed === null || (bed.kind === 'bed' && bed.id === bedId)) delete next[key]; else next[key] = bed;
      return next;
    });
  };

  /**
   * "1 plant": files the guess chosen as one plant, the whole photo, in the Bed list's bed.
   */
  const addOne = async (): Promise<void> => {
    if (sharedTarget === null || (newBed && !bedLabel) || pickOne === 'else' || !rows[pickOne]) return;
    setSaving(true);
    setFailed(null);
    try {
      onFiled(await fileKinds(photo.tray_id, sharedTarget, [{ ...rows[pickOne].item, box: null }], group));
    } catch (error) {
      setFailed(errorMessage(error, t('packets.saveFailed')));
    } finally {
      setSaving(false);
    }
  };

  /**
   * Files a plant for each ticked kind in its bed; the photo leaves the tray with them.
   *
   * @param upToRoom - On Free, add what fits and leave the rest.
   */
  const add = async (upToRoom = false): Promise<void> => {
    if (blocked || sharedTarget === null) return;
    setSaving(true);
    setFailed(null);
    try {
      onFiled(await fileKinds(photo.tray_id, sharedTarget, items, group, upToRoom));
    } catch (error) {
      setFailed(errorMessage(error, t('packets.saveFailed')));
    } finally {
      setSaving(false);
    }
  };

  /** The Bed list, one for the card whichever way it is asked, with "A new bed…" and its name box. */
  const bedList = (
    <>
      <label className="block text-xs text-muted-foreground" htmlFor={`${idBase}-kinds-bed`}>
        {t('packets.bed')}
        <NativeSelect
          id={`${idBase}-kinds-bed`}
          className="mt-1 text-sm text-foreground"
          value={choice ?? NEW_BED}
          onChange={(event) => setBedChoice(event.target.value)}
          disabled={off}
        >
          {locations.map((bed) => <option key={bed.id} value={String(bed.id)}>{bed.name}</option>)}
          <option value={NEW_BED}>{t('packets.newBed')}</option>
        </NativeSelect>
      </label>
      {newBed && (
        <label className="block text-xs text-muted-foreground" htmlFor={`${idBase}-kinds-bed-name`}>
          {t('packets.bedName')}
          <Input
            id={`${idBase}-kinds-bed-name`}
            className="mt-1 text-sm"
            value={bedName}
            onChange={(event) => setBedName(event.target.value)}
            maxLength={limits.LOCATION_NAME_MAX}
            disabled={off}
          />
        </label>
      )}
    </>
  );
  /** The new bed's picture is this photo, and nothing about it is guessed (D78 P4). */
  const coverNote = newBed && bedLabel ? <p className="text-xs text-muted-foreground">{t('review.coverNote', { bed: bedLabel })}</p> : null;

  const open = opened === null ? null : rows.find((row) => row.key === opened) ?? null;
  const openEdit = open ? edits[open.key] ?? startEdit(open, lookup) : null;
  /*
   * The open row's seen name under the type now chosen in its dialog: offered in the Variety list (filed as theirs by
   * that name) only when the server finds no variety of that name under that type.
   */
  const seenOpen = useSeenMatches(open && openEdit ? [{ plantId: openEdit.choice.plantId, name: open.kind.variety }] : []);
  const openMatch = open && openEdit ? seenOpen.matchOf(openEdit.choice.plantId, open.kind.variety) : undefined;
  const offerSeen = Boolean(open?.kind.variety) && openEdit !== null && openEdit.choice.plantId !== null
    && openMatch !== undefined && openMatch?.plantId !== openEdit.choice.plantId;

  return (
    <>
      {/* "Is that right?": several plants, or one. A real radio group; the other side keeps what was set. */}
      <div className="space-y-1.5">
        <p id={`${idBase}-kinds-q`} className="text-sm text-muted-foreground">{t('tray.kinds.sees', { count: kinds.length })}</p>
        <div role="radiogroup" aria-labelledby={`${idBase}-kinds-q`} className="grid grid-cols-2 overflow-hidden rounded-md border border-primary">
          {(['many', 'one'] as const).map((value) => (
            <label
              key={value}
              className="flex min-h-11 cursor-pointer items-center justify-center text-sm text-primary has-[:checked]:bg-primary has-[:checked]:font-bold has-[:checked]:text-primary-foreground has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-ring"
            >
              <input
                type="radio"
                name={`${idBase}-kinds-mode`}
                className="sr-only"
                checked={mode === value}
                onChange={() => { setMode(value); setOpened(null); setFailed(null); }}
                disabled={off}
              />
              {value === 'many' ? t('tray.kinds.nPlants', { count: kinds.length }) : t('tray.kinds.onePlant')}
            </label>
          ))}
        </div>
      </div>

      {mode === 'one' ? (
        <>
          <p className="text-sm text-muted-foreground">{t('tray.kinds.whichOne')}</p>
          <div role="radiogroup" aria-label={t('tray.kinds.whichOne')} className="flex flex-col gap-1.5">
            {[...rows.map((row) => ({ id: row.key as number | 'else', label: capital(row.kind.seen) })), { id: 'else' as const, label: t('tray.kinds.somethingElse') }].map((one) => (
              <label key={one.id} className="flex min-h-11 cursor-pointer items-center gap-3 rounded-sm border px-3 text-sm has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-ring">
                <input
                  type="radio"
                  name={`${idBase}-kinds-one`}
                  className="h-5 w-5 shrink-0 accent-primary"
                  checked={pickOne === one.id}
                  onChange={() => setPickOne(one.id)}
                  disabled={off}
                />
                {one.label}
              </label>
            ))}
          </div>
          {pickOne === 'else' ? somethingElse : (
            <>
              {bedList}
              {coverNote}
              {failed && <p className="text-sm font-medium text-destructive" role="alert"><FailureText text={failed} /></p>}
              {sharedTarget === null && <p className="text-center text-xs text-muted-foreground" role="status">{t('tray.kinds.noBed')}</p>}
              <AddCancelRow onCancel={onCancel} disabled={off}>
                <Button className="w-full" size="lg" onClick={() => void addOne()} disabled={off || sharedTarget === null || (newBed && !bedLabel)}>
                  {saving ? <Spinner size={16} color="currentColor" /> : t('tray.kinds.addPlant', { plant: rows[pickOne]?.plantName ?? '…' })}
                </Button>
              </AddCancelRow>
            </>
          )}
        </>
      ) : (
        <>
          {bedList}

          <ul className="flex flex-col gap-1.5">
            {shown.map((row) => {
              const { kind, key } = row;
              const mine = ownOf(key);
              const edit = edits[key];
              // Changed in its dialog: named from the fields; else as Grow placed it.
              const editPlant = edit ? (edit.choice.typeUnsure ? lookup.cropName(edit.choice.cropId) : lookup.plantName(edit.choice.plantId)) ?? '' : null;
              const editVariety = edit ? (editedItem(edit, kind, lookup).newVariety ?? lookup.varietiesOf(edit.choice.plantId).named.find((v) => v.id === edit.choice.varietyId)?.name ?? null) : null;
              const detail = edit ? [editPlant, editVariety].filter(Boolean).join(' · ') : row.varietyName ? `${row.plantName} · ${row.varietyName}` : row.plantName;
              const ownName = mine?.kind === 'new' ? mine.name.trim() || '…' : mine ? locations.find((loc) => loc.id === mine.id)?.name ?? '' : '';
              return (
                <PacketRow
                  key={`${kind.plant_id ?? 'new'}-${kind.seen}`}
                  id={`${idBase}-kind-${key}`}
                  /* The picture its plant will keep: its own part of the photo, or the whole photo when it has no position. A picture only (B59). */
                  picture={kind.box ? (
                    <PacketCut src={photo.cardUrl} box={kind.box} />
                  ) : (
                    <PacketThumb src={photo.thumbUrl} className="shrink-0" />
                  )}
                  name={capital(kind.seen)}
                  detail={detail}
                  mark={edit ? null : row.isNewPlant ? 'newToGrow' : null}
                  bedLine={mine ? t('tray.kinds.ownBed', { bed: ownName }) : null}
                  ticked={ticked.has(key)}
                  tickable
                  onTick={(on) => tick(key, on)}
                  onOpen={() => setOpened(key)}
                  disabled={off}
                />
              );
            })}
          </ul>

          {/* Said once, under the list: what happens to a plant Grow does not have. */}
          {newPlants.length > 0 && (
            <p className="text-xs text-muted-foreground">
              {t(newPlants.length === 1 ? 'tray.kinds.newPlantNoteOne' : 'tray.kinds.newPlantNote', {
                plant: newPlants[0].plantName.toLocaleLowerCase(),
              })}
            </p>
          )}
          {coverNote}

          {failed && <p className="text-sm font-medium text-destructive" role="alert"><FailureText text={failed} /></p>}
          {blocked && <p className="text-center text-xs text-muted-foreground" role="status">{blocked}</p>}
          {overRoom ? (
            <FreeRoomChoice count={chosen.length} free={free} onAdd={() => void add(true)} disabled={off || Boolean(blocked)} saving={saving} />
          ) : (
            <AddCancelRow onCancel={onCancel} disabled={off}>
              <Button className="w-full" size="lg" onClick={() => void add()} disabled={off || Boolean(blocked)}>
                {saving ? <Spinner size={16} color="currentColor" /> : addLabelFor(places, chosen.length, bedLabel, t)}
              </Button>
            </AddCancelRow>
          )}
        </>
      )}

      {/* "Change this plant": its plant, type, variety and bed, Done, and "Remove this plant". */}
      {open && openEdit && (
        <RowChangeDialog
          open
          onClose={() => setOpened(null)}
          title={t('tray.kinds.changeTitle')}
          picture={open.kind.box ? <PacketCut src={photo.cardUrl} box={open.kind.box} /> : <PacketThumb src={photo.thumbUrl} className="shrink-0" />}
          name={capital(open.kind.seen)}
          value={openEdit.choice}
          onChange={(next) => setEdits((current) => ({ ...current, [open.key]: { ...openEdit, choice: next } }))}
          seen={offerSeen && open.kind.variety ? {
            // A plant tag's words, or what Grow saw (a flower's name).
            label: t(open.kind.label ? 'tray.pile.seenOnTag' : 'tray.pile.seenByGrow', { variety: open.kind.variety }),
            chosen: openEdit.seenChosen && openEdit.choice.varietyId === lookup.varietiesOf(openEdit.choice.plantId).start,
            onChoose: (on) => setEdits((current) => ({ ...current, [open.key]: { ...openEdit, seenChosen: on } })),
          } : null}
          sharedBed={bedLabel}
          bed={ownOf(open.key)}
          onBed={(bed) => setOwnBed(open.key, bed)}
          beds={locations}
          removeLabel={t('tray.kinds.removeThis')}
          onRemove={() => {
            setRemoved((current) => new Set([...current, open.key]));
            setOpened(null);
            toast({ title: t('packets.removed', { name: capital(open.kind.seen) }) });
          }}
          // A plant Grow does not have is a plant already; a changed row needs one, or Type "Not sure".
          doneDisabled={Boolean(edits[open.key]) && openEdit.choice.plantId === null && !openEdit.choice.typeUnsure}
          idPrefix={`${idBase}-kind-change-${open.key}`}
        />
      )}
    </>
  );
};
